import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { v4 as uuidv4 } from "uuid";

import Loading from "../../components/Loading";

import {
  addMarketOfferRecall,
  addOfferComment,
  createOfferNegotiationProposal,
  listAdministrativeAreaTypes,
  listAdministrativeAreas,
  listCommodities,
  listExchangeLocationTypes,
  listExchangeLocations,
  listMarketOffers,
  listMarketOffersReset,
  listMineAggregators,
  listRecallReasons,
  listTraders,
  listUnitsOfMeasure,
  traderRepresentativeDetails,
  updateMarketOffer,
} from "../../actions";
import { OFFER_TYPES } from "../../constants";
import {
  displayErrorMessage,
  formatNumber,
  getCountryObjectFromCache,
  isEmpty,
  valueOrDefault,
} from "../../utils";
import { adaptTrader } from "../../utils/adapters";
import {
  toDeliveryCostPayload,
  type DeliveryCostLocation,
  type DeliveryCostValue,
} from "../../utils/delivery-cost";
import {
  getStoredLanguage,
  getUserLanguage,
  onChangeLanguage,
  orderByLocalName,
} from "../../utils/i18n";
import { Aggregator } from "../AggregatorDetailsModal/types";
import { useOfferStatusCounts } from "../RepresentativeHome/useOfferStatusCounts";
import { buildOfferFilterDefinitions } from "./filterOptions";
import { ExchangeLocation } from "./components/CounterOfferModal";
import { Offer, OfferStatus } from "./components/OfferCard/interfaces";
import { NegotiationProposal } from "./components/OfferCommentsModal/NegotiatedDetailsCard/offer-negotiation-proposal";
import { refreshOfferThread } from "./components/OfferCommentsModal";
import { OffersList } from "./components/OffersList";
import {
  MANAGER_OFFER_TABS,
  OFFER_TAB_LABEL_KEYS,
  OfferTabs,
  REPRESENTATIVE_OFFER_TABS,
} from "./components/OfferTabs";
import { RecallFormValues } from "./components/RecallOfferModal";
import { SendingBackFormValues } from "./components/SendingBackExchangeModal";
import { AddressHolder, NamedEntity, OfferFilterValues, OfferListParams } from "./types";
import {
  buildInitialFilters,
  buildOfferParams,
  DEFAULT_OFFER_FILTERS,
  describeActiveFilters,
  OFFERS_PAGE_SIZE,
  toOfferUpdatePayload,
} from "./utils";
import { Package, RefreshCw, Summary } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { FilterBar } from "@/components/FilterBar";
import { WFPButton } from "@/components/ui/button";

interface CommodityOption {
  id: number;
  name: string;
  name_translations?: { en: string };
}

interface AdminArea extends NamedEntity {
  type: number;
}

interface CurrentUser {
  organization?: number;
  representative?: number;
}

interface TraderRepresentative {
  id?: number;
  language?: string;
}

interface StateProps {
  marketOffers: Offer[];
  marketOffersCount: number;
  isFetching: boolean;
  isFetchingMarketOffers: boolean;
  errorMessageMarketOffers: string;
  errorMessageCurrentUser: string;
  errorMessageTrader: string;
  currentUser: CurrentUser;
  trader: AddressHolder;
  traders: AddressHolder[];
  traderRepresentative: TraderRepresentative;
  commodities: CommodityOption[];
  aggregators: Aggregator[];
  customExchangeLocations: ExchangeLocation[];
  exchangeLocationTypes: NamedEntity[];
  administrativeAreas: AdminArea[];
  administrativeAreaTypes: NamedEntity[];
}

interface DispatchProps {
  listMarketOffers: (params: OfferListParams) => void;
  listMarketOffersReset: () => void;
  listTraders: () => void;
  listCommodities: () => void;
  listMineAggregators: () => void;
  listUnitsOfMeasure: () => void;
  listRecallReasons: () => void;
  listExchangeLocations: () => void;
  listExchangeLocationTypes: () => void;
  listAdministrativeAreas: () => void;
  listAdministrativeAreaTypes: () => void;
  traderRepresentativeDetails: (id: string) => void;
  recallOffer: (comment: string, id: number, reason: number) => Promise<unknown>;
  sendBackExchange: (comment: string, offer: Offer) => Promise<unknown>;
  setOfferRecalledStatus: (offer: Offer) => Promise<unknown>;
  addNegotiationProposal: (
    proposal: Record<string, unknown>,
    offer: Offer
  ) => Promise<Partial<NegotiationProposal>>;
  refreshContent: (offer: Offer) => void;
}

type RepresentativeOffersProps = StateProps &
  DispatchProps &
  RouteComponentProps<
    Record<string, never>,
    Record<string, never>,
    { fromProfileUpdate?: boolean; status?: string }
  > & {
    emptyLayout?: boolean;
    readOnly?: boolean;
  };

const getStoredTraderOrganizationId = () => localStorage.getItem("trader_organization_id");

const getStoredTraderRepresentativeId = () => localStorage.getItem("trader_representative_id");

export const RepresentativeOffers = ({
  marketOffers,
  marketOffersCount,
  isFetching,
  isFetchingMarketOffers,
  errorMessageMarketOffers,
  errorMessageCurrentUser,
  errorMessageTrader,
  currentUser,
  trader,
  traders,
  traderRepresentative,
  commodities,
  aggregators,
  customExchangeLocations,
  exchangeLocationTypes,
  administrativeAreas,
  administrativeAreaTypes,
  history,
  emptyLayout,
  readOnly = false,
  listMarketOffers,
  listMarketOffersReset,
  listTraders,
  listCommodities,
  listMineAggregators,
  listUnitsOfMeasure,
  listRecallReasons,
  listExchangeLocations,
  listExchangeLocationTypes,
  listAdministrativeAreas,
  listAdministrativeAreaTypes,
  traderRepresentativeDetails,
  recallOffer,
  sendBackExchange,
  setOfferRecalledStatus,
  addNegotiationProposal,
  refreshContent,
}: RepresentativeOffersProps) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<OfferFilterValues>(() =>
    buildInitialFilters(history.location?.state)
  );
  const [page, setPage] = useState(1);
  const statusCounts = useOfferStatusCounts();

  const locationState = history.location?.state;
  const selectedLanguage = getUserLanguage();

  const traderId = readOnly
    ? ""
    : String(currentUser.organization || getStoredTraderOrganizationId() || "");
  const isWaitingForCurrentUser = !readOnly && isEmpty(currentUser) && !errorMessageCurrentUser;
  const isContentFetching = isFetching || isWaitingForCurrentUser;
  const representativeErrorMessage = errorMessageCurrentUser || errorMessageTrader;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    listTraders();
    listMineAggregators();
    listCommodities();
    listUnitsOfMeasure();
    listRecallReasons();
    listExchangeLocations();
    listExchangeLocationTypes();
    listAdministrativeAreas();
    listAdministrativeAreaTypes();

    const storedTraderRepresentativeId = getStoredTraderRepresentativeId();
    if (storedTraderRepresentativeId) {
      traderRepresentativeDetails(storedTraderRepresentativeId);
    }

    return () => listMarketOffersReset();
  }, [
    listTraders,
    listMineAggregators,
    listCommodities,
    listUnitsOfMeasure,
    listRecallReasons,
    listExchangeLocations,
    listExchangeLocationTypes,
    listAdministrativeAreas,
    listAdministrativeAreaTypes,
    listMarketOffersReset,
    traderRepresentativeDetails,
  ]);

  useEffect(() => {
    if (readOnly || traderId) {
      listMarketOffers(buildOfferParams(filters, page, traderId));
    }
  }, [filters, page, traderId, readOnly, listMarketOffers]);

  useEffect(() => {
    if (
      currentUser.representative &&
      String(traderRepresentative?.id) !== String(currentUser.representative)
    ) {
      traderRepresentativeDetails(String(currentUser.representative));
    }
  }, [currentUser.representative, traderRepresentative?.id, traderRepresentativeDetails]);

  useEffect(() => {
    if (
      !readOnly &&
      trader?.name &&
      !trader.physical_address &&
      !locationState?.fromProfileUpdate
    ) {
      history.push("/representative/profile", { updateDetails: true });
    }
  }, [history, locationState, readOnly, trader]);

  useEffect(() => {
    const handleLanguageChange = async () => {
      // Profile data may arrive after a top-bar language change. Keep the
      // explicit browser preference as the source of truth once it exists.
      if (getStoredLanguage()) {
        return;
      }

      const language = traderRepresentative?.language;
      if (
        language &&
        selectedLanguage &&
        language !== selectedLanguage &&
        // Don't change the language if we're coming from the profile update
        // form, as the language changes will interfere with each other.
        !locationState?.fromProfileUpdate
      ) {
        await onChangeLanguage({ value: language });
      }
    };
    handleLanguageChange();
  }, [locationState, selectedLanguage, traderRepresentative]);

  const parsedAdministrativeAreas = useMemo(
    () =>
      (administrativeAreas || []).map(area => ({
        ...area,
        correspondingType: administrativeAreaTypes.find(type => type.id === area.type),
      })),
    [administrativeAreas, administrativeAreaTypes]
  );

  const filterDefinitions = useMemo(
    () =>
      buildOfferFilterDefinitions(t, {
        tab: filters.tab,
        commodities,
        aggregators,
        parsedAdministrativeAreas,
      }),
    [t, filters.tab, commodities, aggregators, parsedAdministrativeAreas]
  );

  const activeFilters = [
    ...(filters.tab === "all" ? [] : [t(OFFER_TAB_LABEL_KEYS[filters.tab])]),
    ...describeActiveFilters(filters, filterDefinitions),
  ];

  const handleFilterChange = (patch: Partial<OfferFilterValues>) => {
    setPage(1);
    setFilters(current => ({ ...current, ...patch }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters(DEFAULT_OFFER_FILTERS);
  };

  const refreshList = () => {
    statusCounts.refresh();
    listMarketOffers(buildOfferParams(filters, page, traderId));
  };

  const handleRecall = async (offer: Offer, values: RecallFormValues) => {
    await Promise.all([
      recallOffer(values.recall_comments, offer.id, Number(values.recall_reason)),
      setOfferRecalledStatus(offer),
    ]);
    refreshList();
  };

  const handleSendingBack = (offer: Offer, values: SendingBackFormValues) => {
    const proposal = offer.latest_negotiation_proposal;

    const comment = t("autoComment.sendBackOffer", {
      comment: values.send_back_comments,
      trader: trader?.name,
      quantity:
        proposal.maximum_quantity === proposal.minimum_quantity
          ? formatNumber(proposal.minimum_quantity)
          : `${formatNumber(proposal.minimum_quantity)} - ${formatNumber(proposal.maximum_quantity)}`,
      unit: offer.unit_of_measure.code,
    });

    return sendBackExchange(comment, offer);
  };

  const content = (
    <div className="flex flex-col gap-6">
      <FilterBar
        definitions={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      <OfferTabs
        tabs={readOnly ? MANAGER_OFFER_TABS : REPRESENTATIVE_OFFER_TABS}
        value={filters.tab}
        onChange={tab => handleFilterChange({ tab, status: "" })}
        counts={statusCounts.counts}
        isLoading={statusCounts.isLoading}
      />

      {representativeErrorMessage && displayErrorMessage(representativeErrorMessage)}
      {errorMessageMarketOffers && displayErrorMessage(errorMessageMarketOffers)}

      {isContentFetching || isFetchingMarketOffers ? (
        <Loading isVisible />
      ) : marketOffers.length ? (
        <>
          <p className="text-muted-foreground text-sm">
            {t("home.marketOffers.offersShown", {
              count: marketOffersCount,
              from: (page - 1) * OFFERS_PAGE_SIZE + 1,
              to: (page - 1) * OFFERS_PAGE_SIZE + marketOffers.length,
            })}
          </p>
          <OffersList
            offers={marketOffers}
            totalItems={marketOffersCount}
            page={page}
            onPageChange={setPage}
            traders={traders}
            trader={trader}
            aggregators={aggregators}
            customExchangeLocations={customExchangeLocations}
            exchangeLocationTypes={exchangeLocationTypes}
            administrativeAreas={administrativeAreas}
            readOnly={readOnly}
            refreshList={refreshList}
            refreshContent={refreshContent}
            onValidateRecall={handleRecall}
            onValidateSendingBack={handleSendingBack}
            addCounterOffer={(offer, values) => addNegotiationProposal(values, offer)}
          />
        </>
      ) : (
        <EmptyState
          icon={Package}
          title={t(
            activeFilters.length
              ? "home.marketOffers.noOffersFiltered"
              : "home.marketOffers.noOffers"
          )}
          description={
            activeFilters.length
              ? t("home.marketOffers.filteredDescription", { filters: activeFilters.join(", ") })
              : t(
                  readOnly
                    ? "home.manager.overview.highlights.market.emptyDescription"
                    : "home.marketOffers.emptyDescription"
                )
          }
          action={
            activeFilters.length ? (
              <WFPButton type="button" variant="secondary" onClick={handleClearFilters}>
                {t("filters.clearAll")}
              </WFPButton>
            ) : undefined
          }
        />
      )}
    </div>
  );

  if (emptyLayout) {
    return content;
  }

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-y-3">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-blue-600 bg-blue-100">
              <Summary />
            </span>
            <h1 className="font-semibold text-2xl">{t("marketOfferView.home")}</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {t(
              readOnly
                ? "home.marketOffers.managerOfferProgress"
                : "home.marketOffers.offerProgress"
            )}
          </p>
        </div>
        <WFPButton type="button" variant="secondary" onClick={refreshList}>
          <RefreshCw className="size-4" aria-hidden="true" />
          {t("marketOfferView.refresh")}
        </WFPButton>
      </div>
      {content}
    </main>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  errorMessage: string;
  data: T;
}

interface RootState {
  listMarketOffers: RequestState<{ results?: Offer[]; count?: number }>;
  listTraders: RequestState<{ results?: AddressHolder[] }>;
  listCommodities: RequestState<{ results?: CommodityOption[] }>;
  listMineAggregators: RequestState<{ results?: Aggregator[] }>;
  listUnitsOfMeasure: RequestState<{ results?: NamedEntity[] }>;
  listOfferNegotiationProposals: RequestState<{ results?: NegotiationProposal[] }>;
  listExchangeLocations: RequestState<{ results?: ExchangeLocation[] }>;
  listExchangeLocationTypes: RequestState<{ results?: NamedEntity[] }>;
  listAdministrativeAreas: RequestState<{ results?: AdminArea[] }>;
  listAdministrativeAreaTypes: RequestState<{ results?: NamedEntity[] }>;
  traderRepresentativeDetails: RequestState<TraderRepresentative>;
  userDetail: RequestState<CurrentUser>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  isFetching:
    state.listMarketOffers.isFetching ||
    state.listTraders.isFetching ||
    state.listCommodities.isFetching ||
    state.listMineAggregators.isFetching ||
    state.listUnitsOfMeasure.isFetching ||
    state.traderRepresentativeDetails.isFetching ||
    state.userDetail.isFetching ||
    state.listExchangeLocations.isFetching ||
    state.listExchangeLocationTypes.isFetching ||
    state.listAdministrativeAreaTypes.isFetching ||
    state.listAdministrativeAreas.isFetching,
  isFetchingMarketOffers: state.listMarketOffers.isFetching,
  errorMessageMarketOffers: state.listMarketOffers.errorMessage,
  errorMessageCurrentUser: state.userDetail.errorMessage,
  errorMessageTrader:
    state.listTraders.errorMessage || state.traderRepresentativeDetails.errorMessage,
  marketOffers: state.listMarketOffers.data.results ?? [],
  marketOffersCount: state.listMarketOffers.data.count ?? 0,
  currentUser: state.userDetail.data,
  trader: adaptTrader(state.listTraders.data.results),
  traders: state.listTraders.data.results ?? [],
  traderRepresentative: state.traderRepresentativeDetails.data,
  commodities: orderByLocalName(state.listCommodities.data.results) as CommodityOption[],
  aggregators: state.listMineAggregators.data.results ?? [],
  customExchangeLocations: state.listExchangeLocations.data.results ?? [],
  exchangeLocationTypes: state.listExchangeLocationTypes.data.results ?? [],
  administrativeAreas: state.listAdministrativeAreas.data.results ?? [],
  administrativeAreaTypes: state.listAdministrativeAreaTypes.data.results ?? [],
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown): Promise<unknown> =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listMarketOffers: params => dispatchAction(dispatch, listMarketOffers(params)),
  listMarketOffersReset: () => dispatchAction(dispatch, listMarketOffersReset()),
  listTraders: () => dispatchAction(dispatch, listTraders()),
  listCommodities: () => dispatchAction(dispatch, listCommodities({ page: 1, page_size: 200 })),
  listMineAggregators: () => dispatchAction(dispatch, listMineAggregators()),
  listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
  listRecallReasons: () => dispatchAction(dispatch, listRecallReasons()),
  listExchangeLocations: () => dispatchAction(dispatch, listExchangeLocations()),
  listExchangeLocationTypes: () => dispatchAction(dispatch, listExchangeLocationTypes()),
  listAdministrativeAreaTypes: () => dispatchAction(dispatch, listAdministrativeAreaTypes()),
  listAdministrativeAreas: () =>
    dispatchAction(
      dispatch,
      listAdministrativeAreas({
        page_size: 9999,
        page: 1,
        country: getCountryObjectFromCache().id,
      })
    ),
  traderRepresentativeDetails: id => dispatchAction(dispatch, traderRepresentativeDetails(id)),

  recallOffer: (comment, id, reason) =>
    Promise.all([
      dispatchAction(
        dispatch,
        addMarketOfferRecall({
          trader_market_offer: id,
          recall_comments: comment,
          recall_reason: reason,
        })
      ),
      dispatchAction(
        dispatch,
        addOfferComment({
          trader_market_offer: id,
          text: comment,
          uuid: uuidv4(),
          trader_market_offer_status: OfferStatus.Recalled,
          send_mobile_notification: false,
        })
      ),
    ]),

  sendBackExchange: (comment, offer) =>
    Promise.all([
      dispatchAction(
        dispatch,
        updateMarketOffer(
          offer.id,
          toOfferUpdatePayload({
            ...offer,
            ...(offer.delivery_location === "custom_exchange_location"
              ? { custom_delivery_location: offer.custom_delivery_location_details?.id }
              : {}),
            price: Number(offer.price) || 0,
            status: OfferStatus.ExchangeDetailsDisputed,
          })
        )
      ),
      dispatchAction(
        dispatch,
        addOfferComment({
          trader_market_offer: offer.id,
          text: `${comment}`,
          uuid: uuidv4(),
          trader_market_offer_status: OfferStatus.ExchangeDetailsDisputed,
          send_mobile_notification: false,
        })
      ),
    ]),

  setOfferRecalledStatus: offer =>
    dispatchAction(
      dispatch,
      updateMarketOffer(
        offer.id,
        toOfferUpdatePayload({
          ...offer,
          ...(offer.delivery_location === "custom_exchange_location"
            ? { custom_delivery_location: offer.custom_delivery_location_details?.id }
            : {}),
          price: Number(offer.price) || 0,
          status:
            offer.status === OfferStatus.ExchangeDetailsUpdated
              ? OfferStatus.ExchangeDetailsDisputed
              : OfferStatus.Recalled,
          planned_exchange_time: undefined,
        })
      )
    ),

  addNegotiationProposal: (proposal, offer) => {
    const deliveryLocation = proposal.delivery_location as
      | { value: Record<string, string> | string }
      | undefined;
    const hasCustomExchangeLocation = typeof deliveryLocation?.value === "object";
    const customValue = deliveryLocation?.value as Record<string, string>;

    const adaptedProposal = {
      minimum_quantity: valueOrDefault(proposal.minimum_quantity, ""),
      maximum_quantity: valueOrDefault(
        offer.offer_type === OFFER_TYPES.DIRECT_OFFER
          ? proposal.minimum_quantity
          : proposal.maximum_quantity,
        ""
      ),
      price: valueOrDefault(proposal.price, ""),
      delivery_cost: toDeliveryCostPayload(
        deliveryLocation?.value as DeliveryCostLocation,
        proposal.delivery_cost as DeliveryCostValue
      ),
      text: valueOrDefault(proposal.text, ""),
      planned_exchange_time:
        proposal.planned_exchange_time instanceof Date
          ? proposal.planned_exchange_time.toISOString()
          : undefined,
      delivery_location: hasCustomExchangeLocation
        ? customValue.deliveryLocationType
        : valueOrDefault(deliveryLocation?.value, undefined),
      custom_delivery_location: hasCustomExchangeLocation
        ? customValue.custom_delivery_location
        : undefined,
      custom_delivery_location_type: hasCustomExchangeLocation
        ? customValue.custom_delivery_location_type
        : undefined,
      trader_market_offer: offer.id,
      uuid: uuidv4(),
    };

    return dispatchAction(dispatch, createOfferNegotiationProposal(adaptedProposal)).then(
      () => adaptedProposal as unknown as Partial<NegotiationProposal>
    );
  },

  refreshContent: offer => refreshOfferThread(dispatch, offer),
});

export const ConnectedRepresentativeOffers = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(RepresentativeOffers));

export const WfpManagerOffers = () => <ConnectedRepresentativeOffers readOnly />;
